import React from 'react';
import { connect } from 'react-redux';
import {
  priceFormat,
  getMappedValueFromOrderOrItem,
  truncateWithEllipsis,
  formatOrderDateToLocalTime,
} from '../../../../util';
import {
  setSelectedItem,
  setSelectedNestedSubItems,
  clearSelectedNestedSubItems,
} from '../../../../actions/tools/OrdersSearchActions';
import {
  getSelectedOrder,
  getLoadingOrderId,
  getSelectedLineItemsIds,
  getStatusOfItemsOfSelectedOrderByItemId,
  getLineItemsOfSelectedOrder,
  getOrderingSystem,
  getIsRbiInstalled,
} from '../../../../selectors/tools';
import MDSpinner from 'react-md-spinner';
import {
  getDecisionNodeAtPath,
  extractSelectedTicketFromState,
  getSelectedNestedSubItems,
} from '../../../../extractFromState';
import { DECISION_NODE_ACTIONS } from '../../../../constants/decisionTree';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import {
  LoadingHolder,
  ResultsColumn,
  ResultsColumnGrey,
  CheckBoxColumn,
  Checkbox,
  CheckboxItem,
  NoCheckboxPlaceholder,
  ResultRowOrders,
  ResultRowItems,
  ResultRowSubItems,
  ResultItemContainer,
} from './DTOrderSearchResult.styles';

export class DTOrderSearchResult extends React.Component {
  // find the value of the field of an order or item
  getValue(orderOrItem, field) {
    return getMappedValueFromOrderOrItem(orderOrItem, field, this.props.orderingSystem);
  }

  isOrderSelected() {
    const { result, selectedOrder } = this.props;

    return this.getValue(result, 'orderId') === this.getValue(selectedOrder, 'orderId');
  }

  getCurrencyCode() {
    const { result, orderingSystem } = this.props;

    if (orderingSystem === 'custom_rbi') {
      return this.getValue(result, 'currency.code');
    }

    return this.getValue(result, 'currency');
  }

  handleOrderCheckboxClick() {
    const { clickAction, result } = this.props;
    if (this.hasOrderBeenChecked()) {
      clickAction({});
    } else {
      clickAction(result);
    }
  }

  hasOrderBeenChecked() {
    return this.isOrderSelected() || this.showItemsLoading();
  }

  handleItemCheckboxClick(e, itemId) {
    this.props.setSelectedItem(itemId, e.target.checked);

    // if unchecking RBI item, need to also uncheck all selected subitems
    if (this.props.isRbiInstalled) {
      this.props.clearSelectedNestedSubItems(itemId);
    }
  }

  showItemsLoading() {
    const { loadingId, result, selectedOrder } = this.props;

    return loadingId === this.getValue(result, 'orderId') && loadingId !== this.getValue(selectedOrder, 'orderId');
  }

  generateItemLoadingSection() {
    const { resultList, index } = this.props;

    return (
      <LoadingHolder showUnderline={resultList.length !== index + 1}>
        <MDSpinner color1={'#bcbcbc'} color2={'#bcbcbc'} color3={'#bcbcbc'} color4={'#bcbcbc'} size={20} />
      </LoadingHolder>
    );
  }

  generateOrderCheckboxSection(itemId, isChecked) {
    const { decisionNode } = this.props;
    if (
      [
        DECISION_NODE_ACTIONS.SELECT_ORDER_FOR_CANCELLATION_AND_REFUND,
        DECISION_NODE_ACTIONS.SELECT_ORDER_TO_DETECT_CANCELLATIONS,
        DECISION_NODE_ACTIONS.SELECT_ORDER_FOR_SHIPPING_REFUND,
      ].includes(decisionNode.action)
    ) {
      return <NoCheckboxPlaceholder />;
    }

    return (
      <CheckboxItem type="checkbox" onChange={(e) => this.handleItemCheckboxClick(e, itemId)} checked={isChecked} />
    );
  }

  handleNestedSubItemSelect = (itemId, subItemId, allSubItemsListLength) => {
    const { selectedNestedSubItems } = this.props;
    const currentSelectedSubItemsIds = selectedNestedSubItems[itemId] || [];

    const updatedList = currentSelectedSubItemsIds.includes(subItemId)
      ? currentSelectedSubItemsIds.filter((id) => id !== subItemId)
      : [...currentSelectedSubItemsIds, subItemId];

    if (updatedList.length === allSubItemsListLength) {
      // select item if all its children selected
      this.props.setSelectedItem(itemId, true);
    } else {
      // unselect item if some of its children unselected
      this.props.setSelectedItem(itemId, false);
    }

    this.props.setSelectedNestedSubItems(itemId, updatedList);
  };

  // RBI prices are in cents, need to convert to dollars if RBI ticket is selected
  convertCentsToDollars = (price) => {
    if (this.props.orderingSystem === 'custom_rbi' && typeof price === 'number') {
      return price / 100;
    }

    return price;
  };

  generateRbiSubitems = (list, itemId, isParentItemChecked, allSubItemsSelected) => {
    if (!Array.isArray(list)) {
      return null;
    }

    const { selectedNestedSubItems } = this.props;

    return list.map((subItem, idx) => {
      const subItemId = this.getValue(subItem, 'itemId');
      const uniqueId = `${subItemId}_index_${idx}`;
      const isChecked = isParentItemChecked || get(selectedNestedSubItems, `${itemId}`, []).includes(uniqueId);

      return (
        <ResultRowSubItems key={uniqueId}>
          <CheckboxItem
            type="checkbox"
            onChange={() => this.handleNestedSubItemSelect(itemId, uniqueId, list.length)}
            checked={isChecked}
            disabled={allSubItemsSelected}
          />
          <ResultsColumn>{this.getValue(subItem, 'name')}</ResultsColumn>
        </ResultRowSubItems>
      );
    });
  };

  generateLineItems() {
    const { selectedLineItemsIds, itemStatuses, decisionNode } = this.props;
    let items;
    if (!decisionNode) {
      return null;
    }

    if (
      [
        DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_PARTIAL_REFUND,
        DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_FULL_REFUND,
        DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_COUPON_ISSUE,
        DECISION_NODE_ACTIONS.SELECT_ITEMS_RBI_COUPON_ISSUE,
        DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_LOYALTY_POINTS_ISSUE,
      ].includes(decisionNode.action)
    ) {
      const allSubItemsSelected = [
        DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_FULL_REFUND,
        DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_COUPON_ISSUE,
        DECISION_NODE_ACTIONS.SELECT_ORDER_RBI_LOYALTY_POINTS_ISSUE,
      ].includes(decisionNode.action);

      items = this.props.lineItems.map((item, idx) => {
        const itemId = this.getValue(item, 'itemId');
        const uniqueId = `${itemId}_index_${idx}`;
        const isChecked = allSubItemsSelected || selectedLineItemsIds.indexOf(uniqueId) > -1;

        return (
          <>
            <ResultRowItems key={uniqueId}>
              <CheckBoxColumn>
                <CheckboxItem
                  disabled={allSubItemsSelected}
                  type="checkbox"
                  onChange={(e) => this.handleItemCheckboxClick(e, uniqueId, item.children)}
                  checked={isChecked}
                />
              </CheckBoxColumn>
              <ResultsColumn>{this.getValue(item, 'name')}</ResultsColumn>
              <ResultsColumn>
                {priceFormat(this.convertCentsToDollars(this.getValue(item, 'price')), this.getCurrencyCode())}
                {this.getValue(item, 'points') && ` - ${this.getValue(item, 'points')} Loyalty Points`}
              </ResultsColumn>
            </ResultRowItems>
            {this.generateRbiSubitems(item.children, uniqueId, isChecked, allSubItemsSelected)}
          </>
        );
      });
    } else if (this.props.lineItems) {
      items = this.props.lineItems.map((item, key) => {
        const itemId = this.getValue(item, 'itemId');
        const isChecked = selectedLineItemsIds.indexOf(String(itemId)) > -1;
        const sku = this.getValue(item, 'sku');
        const showSkuAndStatus = !['custom_rbi', 'salesforce'].includes(this.props.orderingSystem);
        const quantityInt = parseInt(this.getValue(item, 'quantity'));
        const quantity = quantityInt ? `${quantityInt}x` : '';

        return (
          <ResultRowItems key={key}>
            <CheckBoxColumn>{this.generateOrderCheckboxSection(itemId, isChecked)}</CheckBoxColumn>
            <ResultsColumn>
              {quantity} {this.getValue(item, 'name')}
            </ResultsColumn>
            {showSkuAndStatus && (
              <>
                <ResultsColumnGrey>{sku}</ResultsColumnGrey>
                <ResultsColumnGrey>{itemStatuses[itemId]}</ResultsColumnGrey>
              </>
            )}
            <ResultsColumnGrey>
              {priceFormat(this.getValue(item, 'price'), this.getCurrencyCode())}
              {this.getValue(item, 'points') && ` - ${this.getValue(item, 'points')} Loyalty Points`}
            </ResultsColumnGrey>
          </ResultRowItems>
        );
      });
    }

    return <ResultItemContainer markAsOrder={this.props.markAsOrder}>{items}</ResultItemContainer>;
  }

  render() {
    const { result, orderingSystem } = this.props;

    return (
      <div>
        <ResultRowOrders checked={this.hasOrderBeenChecked()} markAsOrder={this.props.markAsOrder}>
          <CheckBoxColumn>
            <Checkbox
              type="checkbox"
              checked={this.hasOrderBeenChecked()}
              onChange={(e) => this.handleOrderCheckboxClick(e)}
            />
          </CheckBoxColumn>
          <ResultsColumn width="25%">
            <span>{this.getValue(result, 'orderNumber')}</span>
          </ResultsColumn>
          <ResultsColumn width="25%">
            {priceFormat(this.convertCentsToDollars(this.getValue(result, 'totalPrice')), this.getCurrencyCode())}
          </ResultsColumn>
          <ResultsColumn width="25%">
            {(this.getValue(result, 'fullName') && truncateWithEllipsis(this.getValue(result, 'fullName'), 25)) || '-'}
          </ResultsColumn>
          <ResultsColumn className="name-column" width="25%">
            {formatOrderDateToLocalTime(this.getValue(result, 'createdAt'), orderingSystem)}
          </ResultsColumn>
        </ResultRowOrders>
        {this.isOrderSelected() && this.generateLineItems()}
        {this.showItemsLoading() && this.generateItemLoadingSection()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: getSelectedOrder(state),
  loadingId: getLoadingOrderId(state),
  selectedLineItemsIds: getSelectedLineItemsIds(state),
  itemStatuses: getStatusOfItemsOfSelectedOrderByItemId(state),
  lineItems: getLineItemsOfSelectedOrder(state),
  orderingSystem: getOrderingSystem(state),
  decisionNode: getDecisionNodeAtPath(state),
  selectedTicket: extractSelectedTicketFromState(state),
  selectedNestedSubItems: getSelectedNestedSubItems(state),
  isRbiInstalled: getIsRbiInstalled(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setSelectedItem,
      setSelectedNestedSubItems,
      clearSelectedNestedSubItems,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(DTOrderSearchResult);
